"use client"
import Link from 'next/link';
import React, { useState } from 'react';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Logo from '../../../public/logo.svg';
import NewsletterForm from './NewsletterForm';

const FooterSection = () => {
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const toggleTab = (tab: string) => {
    setActiveTab(activeTab === tab ? null : tab);
  };

  const applicationHostingLinks = [
    { href: '/managed-wordpress-cloud-hosting', title: 'Managed Wordpress Cloud' },
    { href: '/managed-magento-cloud-hosting', title: 'Managed Magento Cloud' },
    { href: '/managed-php-cloud-hosting', title: 'Managed PHP Cloud' },
    { href: '/managed-laravel-cloud-hosting', title: 'Managed Laravel Cloud' },
    { href: '/managed-drupal-cloud-hosting', title: 'Managed Drupal Cloud' },
    { href: '/managed-joomla-cloud-hosting', title: 'Managed Joomla Cloud' },
    { href: '/managed-prestashop-cloud-hosting', title: 'Managed Prestashop Cloud' },
    { href: '/managed-woocommerce-cloud-hosting', title: 'Managed WooCommerce Cloud' },
    { href: '/managed-odoo-hosting', title: 'Managed Odoo Hosting' },
    { href: '/managed-opencart-hosting', title: 'Managed OpenCart Hosting' },
  ];

  const databaseHostingLinks = [
    { href: '/managed-mysql-hosting', title: 'Managed MySQL Hosting' },
    { href: '/managed-redis-hosting', title: 'Managed Redis Hosting' },
    { href: '/managed-mongodb-hosting', title: 'Managed Mongo DB Hosting' },
    { href: '/managed-mariadb-hosting', title: 'Managed MariaDB Hosting' },
    { href: '/managed-oracle-hosting', title: 'Managed Oracle Hosting' },
    { href: '/managed-postgresql-hosting', title: 'Managed PostgreSQL Hosting' },
    { href: '/managed-elasticsearch-hosting', title: 'Managed Elasticsearch Hosting' },
    { href: '/managed-kafka-hosting', title: 'Managed Kafka Hosting' },
    { href: '/managed-prometheus-hosting', title: 'Managed Prometheus Hosting' },
  ];

  const cloudHostingLinks = [
    { href: '/managed-digitalocean-hosting', title: 'Managed DigitalOcean Cloud' },
    { href: '/managed-amazon-cloud-hosting', title: 'Managed Amazon Cloud (AWS)' },
    { href: '/managed-hyper-v-cloud-hosting', title: 'Managed Hyper-V Cloud Hosting' },
    { href: '/managed-kubernetes-hosting', title: 'Managed Kubernetes Hosting' },
  ]

  const serverLinks = [
    { href: '/dedicated-servers-usa', title: 'Managed Dedicated Servers' },
    { href: '/apple-mac-mini-m2-usa', title: 'Apple Mac Servers' },
    { href: '/linux-virtual-private-server-vps-usa', title: 'Linux Virtual Private Server (VPS)' },
    { href: '/windows-virtual-private-server-vps-usa', title: 'Windows Virtual Private Server (VPS)' },
    { href: '/windows-remote-desktop-usa', title: 'Windows Remote Desktop Servers (RDP)' },
    { href: '/seo-rdpvps', title: 'SEO RDP/VPS' },
  ];
  
  const hostingServices = [
    { href: '/linux-shared-hosting-usa', title: 'Linux Shared Hosting' },
    { href: '/windows-shared-hosting-usa', title: 'Windows Shared Hosting' },
    { href: '/linux-reseller-hosting-usa', title: 'Linux Reseller Hosting' },
    { href: '/windows-reseller-hosting-usa', title: 'Windows Reseller Hosting' },
    { href: '/linux-seo-hosting', title: 'Linux SEO Hosting' },
  ];

  const additionalLink = [
    { href: '/acronis-cyber-protect-cloud', title: 'Acronis Cyber Protect Cloud' },
    { href: '/domain-registrations', title: 'Domains' },
    { href: '/buy-google-workspace', title: 'Google Workspace' },
    { href: '/proxies', title: 'Proxies' },
    { href: '/vpn', title: 'VPN' },
    { href: '/ssl', title: 'SSL' },
  ];

  const companyLinks = [
    { href: '/about-us', title: 'About Us' },
    { href: '/contact-us', title: 'Contact Us' },
    { href: '/privacy-policy', title: 'Privacy Policy' },
    { href: '/terms-of-use', title: 'Terms & Conditions' },
    { href: '/dmca', title: 'DMCA' },
    { href: process.env.NEXT_PUBLIC_BLOG_URL, title: 'Blog' },
    { href: 'https://www.shareasale.com/shareasale.cfm?merchantID=68808', title: 'Affiliates' },
  ];

  const socialLinks = [
    { href: process.env.NEXT_PUBLIC_FACEBOOK_LINK, title: 'Facebook' },
    { href: process.env.NEXT_PUBLIC_TWITTER_LINK, title: 'Twitter' },
    { href: process.env.NEXT_PUBLIC_LINKEDIN_LINK, title: 'Linkedin' },
    { href: process.env.NEXT_PUBLIC_INSTAGRAM_LINK, title: 'Instagram' },
    { href: process.env.NEXT_PUBLIC_YOUTUBE_LINK, title: 'Youtube' },
    { href: process.env.NEXT_PUBLIC_PINTEREST_LINK, title: 'Pinterest' },
    { href: process.env.NEXT_PUBLIC_TIKTOK_LINK , title: 'Tiktok' }
  ];

  return (
    <footer className="ftrBg">
      <div className="container">
        <div className="row mb-3">
            <div className="col-md-6">
              <Link className="navbar-brand my-3" href="/">
                <Image loading="lazy" className="d-inline-block my-3" src={Logo} width="200" height="40" alt="Logo" />
              </Link>
            </div>
            <div className="col-md-6"> 
              <NewsletterForm />
            </div>
        </div>

        {/* Mobile Tabs */}
        <div className="footer-tabs d-md-none">
          {[
            { title: 'Application Hosting', links: applicationHostingLinks },
            { title: 'Database Hosting', links: databaseHostingLinks },
            { title: 'Cloud Hosting', links: cloudHostingLinks },
            { title: 'Servers', links: serverLinks },
            { title: 'Hosting', links: hostingServices },
            { title: 'Additional', links: additionalLink },
            { title: 'Company', links: companyLinks },
            { title: 'Social Media', links: socialLinks },
          ].map((tab, index) => (
            <div key={index} className="footer-tab">
              <span className="fw-bold fs-5 text-white text-decoration-none border-bottom py-2" onClick={() => toggleTab(tab.title)}  >
              <h6 className='fw-bold fs-5 text-white border-bottom py-2'>{tab.title} <FontAwesomeIcon icon={faChevronDown}  className='float-end'/></h6>
              </span>
              {activeTab === tab.title && (
                <ul className="ftrLinks">
                  {tab.links.map((link, i) => (
                    <li key={i}>
                      <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Desktop Footer */}
        <div className="footer-content d-none d-md-block">
          <div className="row">
            <div className="col-md-3">
              <h5 className='fw-bold fs-5 text-white border-bottom py-2'>Application Hosting</h5>
              <ul className="ftrLinks">
                {applicationHostingLinks.map((link, i) => (
                  <li key={i}>
                    <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                  </li>
                ))}
              </ul>

              <h5 className='fw-bold fs-5 text-white border-bottom py-2'>Hosting</h5>
              <ul className="ftrLinks">
                {hostingServices.map((link, i) => (
                  <li key={i}>
                    <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-md-3">
              <h5 className='fw-bold fs-5 text-white border-bottom py-2'>Database Hosting</h5>
              <ul className="ftrLinks">
                {databaseHostingLinks.map((link, i) => (
                  <li key={i}>
                    <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                  </li>
                ))}
              </ul>

              <h5 className='fw-bold fs-5 text-white border-bottom py-2'>Additional</h5>
              <ul className="ftrLinks">
                {additionalLink.map((link, i) => (
                  <li key={i}>
                    <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-md-3">
              <h5 className='fw-bold fs-5 text-white  border-bottom py-2'>Cloud Hosting</h5>
              <ul className="ftrLinks">
                {cloudHostingLinks.map((link, i) => (
                  <li key={i}>
                    <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                  </li>
                ))}
              </ul>

              
              <h5 className='fw-bold fs-5 text-white border-bottom py-2  mt-lg-3'>Company</h5>
              <ul className="ftrLinks">
                {companyLinks.map((link, i) => (
                  <li key={i}>
                    <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-md-3">
              <h5 className='fw-bold fs-5 text-white  border-bottom py-2'>Servers</h5>
              <ul className="ftrLinks">
                {serverLinks.map((link, i) => (
                  <li key={i}>
                    <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                  </li>
                ))}
              </ul>

              <h5 className='fw-bold fs-5 text-white  border-bottom py-2'>Social Media</h5>
              <ul className="ftrLinks">
                {socialLinks.map((link, i) => (
                  <li key={i}>
                    <Link href={link.href} className="text-decoration-none text-white">{link.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center mt-4">
          <span className="text-white">© TEMOK. All Rights Reserved.</span>
        </div>
      </div>
    </footer>    
  );
};

export default FooterSection;
